import { useState, useEffect, useCallback, useRef } from "react";
import { X, Shield, BarChart3, ChevronDown } from "lucide-react";

const GA_ID = "G-F6WCDSHQSX";
const CONSENT_KEY = "cookie_consent";

/* ─── Consent helpers ─── */
function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    // Migrate old string format
    if (raw === "accepted") return { analytics: true };
    if (raw === "declined") return { analytics: false };
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveConsent(analytics) {
  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ analytics, timestamp: new Date().toISOString() })
  );
}

/* ─── Google Analytics helpers ─── */
function loadGA() {
  if (window._cacGALoaded) return;
  window["ga-disable-" + GA_ID] = false;
  const s = document.createElement("script");
  s.id = "cac-ga-script";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID);
  window._cacGALoaded = true;
}

function disableGA() {
  window["ga-disable-" + GA_ID] = true;
  const s = document.getElementById("cac-ga-script");
  if (s) s.remove();
  window._cacGALoaded = false;
  // Clear GA cookies
  const domains = [window.location.hostname, "." + window.location.hostname];
  document.cookie.split(";").forEach((c) => {
    const name = c.split("=")[0].trim();
    if (name.startsWith("_ga") || name.startsWith("_gid") || name.startsWith("_gat")) {
      domains.forEach((d) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${d}`;
      });
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    }
  });
}

/* ─── Component ─── */
export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const modalRef = useRef(null);
  const prevFocus = useRef(null);

  // On mount — check existing consent or show banner
  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      const t = setTimeout(() => setShowBanner(true), 1200);
      return () => clearTimeout(t);
    }
    if (consent.analytics) loadGA();
    else disableGA();
  }, []);

  // Listen for footer "Cookie Settings" click
  useEffect(() => {
    const handler = () => {
      const consent = getConsent();
      setAnalyticsOn(consent?.analytics || false);
      prevFocus.current = document.activeElement;
      setShowBanner(false);
      setShowPrefs(true);
    };
    window.addEventListener("openCookieSettings", handler);
    return () => window.removeEventListener("openCookieSettings", handler);
  }, []);

  // Focus management
  useEffect(() => {
    if (showPrefs && modalRef.current) modalRef.current.focus();
    if (!showPrefs && prevFocus.current) {
      prevFocus.current.focus();
      prevFocus.current = null;
    }
  }, [showPrefs]);

  // Escape key closes preferences
  useEffect(() => {
    if (!showPrefs) return;
    const handler = (e) => { if (e.key === "Escape") setShowPrefs(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showPrefs]);

  const acceptAll = useCallback(() => {
    saveConsent(true);
    loadGA();
    setShowBanner(false);
    setShowPrefs(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    saveConsent(false);
    disableGA();
    setShowBanner(false);
    setShowPrefs(false);
  }, []);

  const savePreferences = useCallback(() => {
    saveConsent(analyticsOn);
    if (analyticsOn) loadGA();
    else disableGA();
    setShowPrefs(false);
  }, [analyticsOn]);

  const openPrefs = useCallback(() => {
    const consent = getConsent();
    setAnalyticsOn(consent?.analytics || false);
    prevFocus.current = document.activeElement;
    setShowBanner(false);
    setShowPrefs(true);
  }, []);

  return (
    <>
      {/* ═══ CONSENT BANNER ═══ */}
      {showBanner && (
        <div
          data-testid="cookie-consent-banner"
          role="dialog"
          aria-label="Cookie consent"
          className="fixed bottom-0 left-0 right-0 z-[9998] bg-white border-t border-zinc-200 shadow-[0_-4px_24px_rgba(0,0,0,0.08)] p-5 md:p-6 animate-slide-up"
        >
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-start gap-5">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-foreground text-sm mb-1.5">Cookie Preferences</p>
                <p className="text-muted-foreground text-[13px] leading-relaxed">
                  We use essential technologies to keep the website working and optional analytics to understand how the site is used. You can accept analytics, reject non-essential technologies or manage your preferences.
                </p>
              </div>
              <div className="flex flex-wrap gap-2.5 flex-shrink-0 w-full md:w-auto">
                <button
                  onClick={rejectNonEssential}
                  data-testid="cookie-reject-btn"
                  className="px-5 py-2.5 text-sm font-bold text-foreground border border-zinc-200 hover:border-zinc-400 rounded-[10px] transition-colors cursor-pointer"
                >
                  Reject Non-Essential
                </button>
                <button
                  onClick={openPrefs}
                  data-testid="cookie-manage-btn"
                  className="px-5 py-2.5 text-sm font-bold text-foreground border border-zinc-200 hover:border-zinc-400 rounded-[10px] transition-colors cursor-pointer"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={acceptAll}
                  data-testid="cookie-accept-btn"
                  className="px-5 py-2.5 text-sm font-bold bg-primary text-white hover:bg-primary/90 rounded-[10px] transition-colors cursor-pointer"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PREFERENCES MODAL ═══ */}
      {showPrefs && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fade-in"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPrefs(false); }}
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
          <div
            ref={modalRef}
            tabIndex={-1}
            role="dialog"
            aria-label="Cookie preferences"
            data-testid="cookie-preferences-modal"
            className="relative bg-white rounded-2xl max-w-md w-full max-h-[85vh] overflow-y-auto shadow-2xl z-10"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 pt-6 pb-4 flex items-start justify-between rounded-t-2xl z-10">
              <div>
                <h2 className="text-lg font-extrabold text-foreground">Cookie Preferences</h2>
                <p className="text-[13px] text-muted-foreground mt-1">Manage how we use technologies on this website.</p>
              </div>
              <button
                onClick={() => setShowPrefs(false)}
                className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors"
                aria-label="Close cookie preferences"
                data-testid="cookie-prefs-close"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            {/* Categories */}
            <div className="p-6 space-y-4">
              {/* Essential */}
              <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2.5">
                    <Shield className="w-[18px] h-[18px] text-primary" />
                    <span className="text-sm font-bold text-foreground">Essential</span>
                  </div>
                  <span className="text-xs font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full">Always Active</span>
                </div>
                <p className="text-[13px] text-muted-foreground leading-relaxed">Required for core website functionality and remembering your privacy choices.</p>
              </div>

              {/* Analytics */}
              <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2.5">
                    <BarChart3 className="w-[18px] h-[18px] text-primary" />
                    <span className="text-sm font-bold text-foreground">Analytics</span>
                  </div>
                  <button
                    role="switch"
                    aria-checked={analyticsOn}
                    aria-label="Toggle analytics cookies"
                    data-testid="cookie-analytics-toggle"
                    onClick={() => setAnalyticsOn(!analyticsOn)}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${analyticsOn ? "bg-primary" : "bg-zinc-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${analyticsOn ? "translate-x-5" : "translate-x-0"}`} />
                  </button>
                </div>
                <p className="text-[13px] text-muted-foreground leading-relaxed">Helps us understand how visitors use the website so we can improve it.</p>
              </div>
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white border-t border-zinc-100 px-6 py-4 rounded-b-2xl flex flex-col sm:flex-row gap-2.5">
              <button
                onClick={rejectNonEssential}
                data-testid="cookie-prefs-reject-btn"
                className="flex-1 px-4 py-2.5 text-sm font-bold text-foreground border border-zinc-200 hover:border-zinc-400 rounded-[10px] transition-colors cursor-pointer"
              >
                Reject Non-Essential
              </button>
              <button
                onClick={savePreferences}
                data-testid="cookie-prefs-save-btn"
                className="flex-1 px-4 py-2.5 text-sm font-bold bg-foreground text-white hover:bg-zinc-800 rounded-[10px] transition-colors cursor-pointer"
              >
                Save Preferences
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-prefs-accept-btn"
                className="flex-1 px-4 py-2.5 text-sm font-bold bg-primary text-white hover:bg-primary/90 rounded-[10px] transition-colors cursor-pointer"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
